import { useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { GetMeProfileDocument } from '@/__generated__/graphql';
import {
  AccountMigrationRequiredField,
  IdpProfilePreviewField,
  type IdpSignInProfilePreview,
  ResolveIdpSignInStatus,
} from '@/__generated__/idp/graphql';
import { useGenderOptions } from '@/blocks/shared/profile/hooks/use-gender-options';
import { Header } from '@/components/layout/header';
import { ModalPortal } from '@/components/layout/modals/ModalPortal';
import { Button } from '@/components/ui/button/Button';
import { IconAlertCircle } from '@/components/ui/icons';
import CheckBox from '@/components/ui/input/CheckBox';
import InputWithLabel from '@/components/ui/input/InputWithLabel';
import { PATHS } from '@/constants/path';
import type { MigrationSubmitValues } from '@/providers/auth-provider';
import { cn } from '@/utils/cn';
import { convertPrefecture } from '@/utils/convert-prefecture';

const PASSWORD_MIN_LENGTH = 12;

type Props = {
  isOpen: boolean;
  /** 移行パス(B)は MigrationSubmitValues を渡す。IdP 直接パス（省略版）は引数なしで呼ぶ。 */
  onClick: (values?: MigrationSubmitValues) => void | Promise<void>;
  authStatus?: ResolveIdpSignInStatus | null;
  profilePreview?: IdpSignInProfilePreview[];
  variant?: 'migration' | 'registration';
  /** accountMigrationRequirements の必須項目。MARKETING_OPT_IN の有無で受け取りチェックの表示を制御する。 */
  requiredFields?: AccountMigrationRequiredField[];
  /** migrateCurrentAccountToIdp の 422 をパスワード欄へ差し戻すサーバーエラー */
  passwordServerError?: string | null;
};

type TermsFormValues = {
  agreeToTerms: boolean;
  password: string;
  passwordConfirm: string;
  marketingOptIn: boolean;
};

export const MigrationTransferModal = ({
  isOpen,
  onClick,
  authStatus,
  profilePreview = [],
  variant = 'migration',
  requiredFields = [],
  passwordServerError = null,
}: Props) => {
  const t = useTranslations();
  const locale = useLocale();
  const { genderOptions } = useGenderOptions();
  const isMigrationVariant = variant === 'migration';
  const showMarketingOptIn =
    requiredFields.length === 0 ||
    requiredFields.includes(AccountMigrationRequiredField.MarketingOptIn);

  // AccountFoundUnlinked の省略版は profilePreview だけで描画し me を使わないため、
  // KLEW セッションを持たない IdP 直接パス（S4）で GetMe が Unauthorized になり
  // アクセスエラーが出るのを避けるためにクエリをスキップする。
  const isAccountFoundUnlinked = authStatus === ResolveIdpSignInStatus.AccountFoundUnlinked;
  const { data } = useQuery(GetMeProfileDocument, {
    variables: { isRegistered: true },
    skip: !isOpen || isAccountFoundUnlinked,
  });
  const me = data?.me;
  const fullName = [me?.realFamilyName, me?.realFirstName].filter(Boolean).join(' ');
  const fullNameKana = [me?.realFamilyNameKana, me?.realFirstNameKana].filter(Boolean).join(' ');
  const birthday =
    me?.birthYear && me?.birthMonth && me?.birthDay
      ? `${me.birthYear}/${String(me.birthMonth).padStart(2, '0')}/${String(me.birthDay).padStart(2, '0')}`
      : '';
  const prefectureLabel = me?.prefecture
    ? (convertPrefecture(me.prefecture, locale, { includeSuffix: true }) ?? '')
    : '';
  const genderLabel = me?.gender
    ? (genderOptions.find((option) => option.value === me.gender)?.label ?? '')
    : '';

  const validationSchema: yup.ObjectSchema<TermsFormValues> = useMemo(
    () =>
      yup.object().shape({
        agreeToTerms: yup.boolean().oneOf([true]).required(),
        marketingOptIn: yup.boolean().default(true).required(),
        password: isMigrationVariant
          ? yup
              .string()
              .required(t('common-error-none'))
              .min(PASSWORD_MIN_LENGTH, t('livenode-id-migration-password-error-min'))
          : yup.string().default('').defined(),
        passwordConfirm: isMigrationVariant
          ? yup
              .string()
              .required(t('common-error-none'))
              .oneOf([yup.ref('password')], t('livenode-id-migration-password-error-mismatch'))
          : yup.string().default('').defined(),
      }),
    [isMigrationVariant, t],
  );

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<TermsFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      agreeToTerms: false,
      password: '',
      passwordConfirm: '',
      marketingOptIn: true,
    },
  });

  const formatUserData = (
    preview?: IdpSignInProfilePreview,
    key: 'incomingValue' | 'currentValue' = 'incomingValue',
  ): string => {
    const value = preview?.[key];
    if (!preview || !value) return '';
    switch (preview.field) {
      case IdpProfilePreviewField.Gender:
        return genderOptions.find((option) => option.value === value)?.label ?? '';
      case IdpProfilePreviewField.DateOfBirth:
        return dayjs(value).format('YYYY/MM/DD');
      case IdpProfilePreviewField.Prefecture:
        return convertPrefecture(value, locale, { includeSuffix: true }) ?? '';
      default:
        return value;
    }
  };

  const watchedAgreeToTerms = watch('agreeToTerms');

  const isAllFilled = Boolean(watchedAgreeToTerms);

  // パスワードセクションのバリデーション（最小文字数＋再入力一致）。
  // 通過している場合は背景のピンクを消す。
  // watch は登録前の一瞬 undefined を返し得るため ?? '' で防御する。
  const watchedPassword = watch('password') ?? '';
  const watchedPasswordConfirm = watch('passwordConfirm') ?? '';
  const isPasswordSectionValid =
    watchedPassword.length >= PASSWORD_MIN_LENGTH && watchedPassword === watchedPasswordConfirm;

  // AccountFoundUnlinked（省略版）の続行ボタンは handleSubmit を経由しないため
  // react-hook-form の isSubmitting が立たない。多重送信防止用にローカルで管理する。
  const [isUnlinkedSubmitting, setIsUnlinkedSubmitting] = useState(false);

  const handleUnlinkedClick = async () => {
    if (isUnlinkedSubmitting) return;
    setIsUnlinkedSubmitting(true);
    try {
      await onClick();
    } finally {
      setIsUnlinkedSubmitting(false);
    }
  };

  const onSubmit: SubmitHandler<TermsFormValues> = async (data) => {
    if (isMigrationVariant) {
      // 移行パス(B): migrateCurrentAccountToIdp を実行する。422 は passwordServerError で差し戻される。
      await onClick({
        password: data.password,
        termsAccepted: data.agreeToTerms,
        marketingOptIn: showMarketingOptIn ? data.marketingOptIn : false,
      });
    } else {
      // 登録/直接パス: onClick も async（handleMigrationSubmit）なので await して
      // isSubmitting を送信完了まで維持し、多重送信を防ぐ。
      await onClick();
    }
  };

  // タイトルと導入ボックスはフル版・省略版で共通
  const titleAndIntro = (
    <>
      <p className='whitespace-pre-line text-center text-head-24 text-kl-black-deep'>
        {t('livenode-id-migration-title')}
      </p>
      <div className='flex flex-col items-center gap-4 rounded-lg bg-container-accent px-5 pb-6 pt-5'>
        {/* 公開SVGは画像最適化(dangerouslyAllowSVG未設定)を通さず直接配信するため img を使用 */}
        <img src={'/images/sign-up/transfer.svg'} alt={'移行アイコン'} width={234} height={96} />
        <p className='text-body-14'>
          {t(
            isAccountFoundUnlinked
              ? 'livenode-id-migration-introduction_not_detail_description'
              : 'livenode-id-migration-introduction_description',
          )}
        </p>
        <div className='flex items-center justify-center gap-1'>
          <IconAlertCircle width='16' height='16' color='#999999' />
          <a
            href={PATHS.LIVENODE_ID_ABOUT}
            target='_blank'
            rel='noopener noreferrer'
            className='text-body-14 text-foreground-muted underline'
          >
            {t('livenode-id-about_button')}
          </a>
        </div>
      </div>
    </>
  );

  if (isAccountFoundUnlinked) {
    const birthdayPreview = profilePreview.find(
      (p) => p.field === IdpProfilePreviewField.DateOfBirth,
    );
    const prefecturePreview = profilePreview.find(
      (p) => p.field === IdpProfilePreviewField.Prefecture,
    );
    const genderPreview = profilePreview.find((p) => p.field === IdpProfilePreviewField.Gender);

    return (
      <ModalPortal isOpen={isOpen} withHeaderLayout={true}>
        <div className='w-full'>
          <Header logo bgVariant='gray-deep' />
          <div className='flex flex-col gap-8 px-5 py-9'>
            {titleAndIntro}
            <div className='space-y-2'>
              <p className='pl-2 text-label-16'>
                {t('livenode-id-migration-target-user-info_title')}
              </p>
              <p className='whitespace-pre-line text-body-14 text-kl-black-deep'>
                {t('livenode-id-account-found-unlinked_description')}
              </p>
              <div className='rounded-md border border-kl-gray-subtle px-5 py-3'>
                <ProfilePreviewRow
                  label={t('sign_up-profile-birthday')}
                  value={formatUserData(birthdayPreview)}
                  currentLabel={t('livenode-id-account-found-unlinked-current-birthday', {
                    value: formatUserData(birthdayPreview, 'currentValue'),
                  })}
                />
                <ProfilePreviewRow
                  label={t('livenode-id-migration-user-prefecture_label')}
                  value={formatUserData(prefecturePreview)}
                  currentLabel={t('livenode-id-account-found-unlinked-current-prefecture', {
                    value: formatUserData(prefecturePreview, 'currentValue'),
                  })}
                />
                <ProfilePreviewRow
                  label={t('sign_up-profile-gender')}
                  value={formatUserData(genderPreview)}
                  currentLabel={t('livenode-id-account-found-unlinked-current-gender', {
                    value: formatUserData(genderPreview, 'currentValue'),
                  })}
                />
              </div>
            </div>
            <div className='px-5 py-6'>
              <Button
                type='button'
                onClick={handleUnlinkedClick}
                label={t('common-continue')}
                color='gradient'
                fullWidth
                disabled={isUnlinkedSubmitting}
              />
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal isOpen={isOpen} withHeaderLayout={true}>
      <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
        <Header logo bgVariant='gray-deep' />
        <div className='flex flex-col gap-8 px-5 py-9'>
          {titleAndIntro}
          <div className='space-y-2'>
            <p className='pl-2 text-label-16'>
              {t('livenode-id-migration-target-user-info_title')}
            </p>
            <div className='rounded-md border border-kl-gray-subtle px-5 py-3'>
              <ProfileRow label={t('common-profile_image')}>
                <Image
                  src={me?.iconImage?.url || '/images/ui/icons/default_user_icon.svg'}
                  alt={t('common-profile_image')}
                  width={72}
                  height={72}
                  className='rounded-full'
                />
              </ProfileRow>
              <ProfileRow label={t('livenode-id-migration-user-name_label')}>
                <span className='text-right text-body-14'>
                  {t('livenode-id-migration-user-name_value', { ユーザーの氏名: fullName })}
                </span>
              </ProfileRow>
              <ProfileRow label={t('livenode-id-migration-user-name-kana_label')}>
                <span className='text-right text-body-14'>
                  {t('livenode-id-migration-user-name-kana_value', {
                    ユーザーの名前のフリガナ: fullNameKana,
                  })}
                </span>
              </ProfileRow>
              <ProfileRow label={t('sign_up-profile-birthday')}>
                <span className='text-right text-body-14'>{birthday}</span>
              </ProfileRow>
              <ProfileRow label={t('sign_up-profile-nickname')}>
                <span className='text-right text-body-14'>
                  {t('livenode-id-migration-user-nickname_value', {
                    ユーザーのニックネーム: me?.name ?? '',
                  })}
                </span>
              </ProfileRow>
              <ProfileRow label={t('livenode-id-migration-user-prefecture_label')}>
                <span className='text-right text-body-14'>{prefectureLabel}</span>
              </ProfileRow>
              <ProfileRow label={t('sign_up-profile-gender')}>
                <span className='text-right text-body-14'>{genderLabel}</span>
              </ProfileRow>
            </div>
          </div>
          {isMigrationVariant && (
            <div className='space-y-2'>
              {/* NOTE: タイトル・注意文はピンク背景の外（白地） */}
              <div className='space-y-1.5 px-2'>
                <p className='text-label-16'>{t('livenode-id-migration-password_section_title')}</p>
                <p className='text-body-14'>{t('livenode-id-migration-password-caution_label')}</p>
              </div>
              {/* NOTE: 入力欄だけがピンク背景の箱。バリデーション通過時はピンクを消す */}
              <div
                className={cn(
                  'space-y-4 rounded-lg border border-stroke-subtle p-5',
                  !isPasswordSectionValid && 'bg-func-primary/10',
                )}
              >
                <InputWithLabel
                  type='password'
                  label={t('livenode-id-migration-password_label')}
                  required
                  requiredVariant='badge'
                  register={register('password')}
                  error={errors.password}
                />
                <InputWithLabel
                  type='password'
                  label={t('livenode-id-migration-password-confirm_label')}
                  required
                  requiredVariant='badge'
                  register={register('passwordConfirm')}
                  error={errors.passwordConfirm}
                />
                {passwordServerError && (
                  <p className='text-sm text-red-600'>{passwordServerError}</p>
                )}
              </div>
            </div>
          )}
          <div className='space-y-2'>
            <span className='pl-2 text-label-16'>{t('livenode-id-terms_title')}</span>
            <div className='h-56 w-full overflow-y-auto rounded-md border border-stroke-subtle px-3 py-2'>
              <p className='whitespace-pre-line text-body-12'>{t('livenode-id-terms_body')}</p>
            </div>
          </div>
          <div
            className={cn(
              'flex flex-col gap-4 rounded-lg border border-stroke-subtle p-5',
              isAllFilled ? 'bg-white' : 'bg-func-primary/10',
            )}
          >
            <CheckBox register={register('agreeToTerms', { required: true })} size='lg'>
              <span className='text-label-14'>{t('livenode-id-membership-rules_check')}</span>
            </CheckBox>
            {showMarketingOptIn && (
              <CheckBox register={register('marketingOptIn')} size='lg'>
                <span className='text-label-14'>{t('livenode-id-migration-recommend_check')}</span>
              </CheckBox>
            )}
          </div>
          <div className='px-5 py-6'>
            <Button
              type='submit'
              label={t('common-continue')}
              color='gradient'
              fullWidth
              disabled={!isValid || isSubmitting}
            />
          </div>
        </div>
      </form>
    </ModalPortal>
  );
};

const ProfileRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className='flex items-center justify-between gap-4 border-b border-dotted border-stroke-subtle py-4 first:pt-0 last:border-0 last:pb-0'>
    <span className='text-body-14'>{label}</span>
    {children}
  </div>
);

const ProfilePreviewRow = ({
  label,
  value,
  currentLabel,
}: {
  label: string;
  value: string;
  currentLabel: string;
}) => (
  <div className='border-b border-dotted border-stroke-subtle py-4 first:pt-0 last:border-0 last:pb-0'>
    <div className='flex items-center justify-between gap-4'>
      <span className='text-label-16'>{label}</span>
      <span className='text-right text-label-16'>{value}</span>
    </div>
    <p className='mt-2 text-body-12 text-foreground-muted'>{currentLabel}</p>
  </div>
);
